/**
 * Resolve an accessible label for a native <select> that Select2 has enhanced.
 * Priority: associated <label[for]> element → aria-label attribute → data-placeholder.
 * When a label element is found and lacks an id, one is assigned so it can be
 * referenced via aria-labelledby.
 */
function resolveSelect2Label($select) {
  const id = $select.attr('id');
  if (id) {
    const $label = $(`label[for="${id}"]`);
    if ($label.length) {
      if (!$label.attr('id')) {
        $label.attr('id', `${id}-label`);
      }
      return { labelId: $label.attr('id'), text: $label.text().trim() };
    }
  }
  const ariaLabel = $select.attr('aria-label');
  if (ariaLabel) return { labelId: null, text: ariaLabel };
  const placeholder = $select.attr('data-placeholder');
  if (placeholder) return { labelId: null, text: placeholder };
  return null;
}

/**
 * Apply an accessible name to the Select2 combobox and its inline search field
 * (the search <input> that lives inside the multiple-selection chip list).
 * Called on every DOM sync pass so it stays current even after late init.
 */
function fixSelect2ElementLabels($select) {
  const labelInfo = resolveSelect2Label($select);
  if (!labelInfo) return;
  const $combobox = $select.next('.select2-container').find('.select2-selection[role="combobox"]');
  if (!$combobox.length) return;
  if (labelInfo.labelId) {
    $combobox.attr('aria-labelledby', labelInfo.labelId);
  } else {
    $combobox.attr('aria-label', labelInfo.text);
  }
  $combobox.find('.select2-search__field').attr('aria-label', labelInfo.text);
}

function syncSelect2Labels() {
  $('select.select2-hidden-accessible').each(function () {
    fixSelect2ElementLabels($(this));
  });
}

/**
 * Align Select2's combobox markup with the WAI-ARIA combobox + listbox pattern:
 * - aria-haspopup="listbox" (Select2 ships aria-haspopup="true")
 * - aria-controls on the combobox pointing at the listbox id (Select2 only sets aria-owns)
 * - aria-label on the listbox and dropdown search field (only reachable once the dropdown
 *   is open because attachBody moves the dropdown under <body>)
 */
function applySelect2ListboxAccessibility($select) {
  const $container = $select.next('.select2-container');
  const $combobox = $container.find('.select2-selection[role="combobox"]');
  if (!$combobox.length) {
    return;
  }
  $combobox.attr('aria-haspopup', 'listbox');
  const instance = $select.data('select2');
  const predictedId = instance && instance.id ? `${instance.id}-results` : null;
  let listboxId = predictedId && document.getElementById(predictedId) ? predictedId : null;
  if (!listboxId) {
    listboxId = $container.find('.select2-results__options[role="listbox"]').attr('id');
  }
  if (listboxId) {
    $combobox.attr('aria-controls', listboxId);
    const labelInfo = resolveSelect2Label($select);
    if (labelInfo) {
      const $listbox = $(`#${listboxId}`);
      if ($listbox.length) {
        $listbox.attr('aria-label', labelInfo.text);
        $listbox.closest('.select2-dropdown')
          .find('.select2-search--dropdown .select2-search__field')
          .attr('aria-label', labelInfo.text);
      }
    }
  }
}

function clearSelect2ListboxControls($select) {
  const $combobox = $select.next('.select2-container').find('.select2-selection[role="combobox"]');
  $combobox.removeAttr('aria-controls');
}

function syncSelect2ComboboxHaspopup() {
  $('select.select2-hidden-accessible').each(function () {
    const $combobox = $(this).next('.select2-container').find('.select2-selection[role="combobox"]');
    if ($combobox.length) {
      $combobox.attr('aria-haspopup', 'listbox');
    }
  });
}

/**
 * Optgroup results use a nested <ul class="select2-results__options--nested"> with no role.
 * That keeps native list/listitem semantics between role="group" and role="option", so
 * validators report "role not inside required context" for options. Presentation removes the
 * nested list from the a11y tree so options stay under the outer listbox (APG grouped pattern).
 */
function fixSelect2NestedResultsLists() {
  $('.select2-results__options--nested').attr('role', 'presentation');
}

let select2WcagObserverInitialized = false;

function initSelect2WcagObserver() {
  if (select2WcagObserverInitialized || typeof MutationObserver === 'undefined') {
    return;
  }
  select2WcagObserverInitialized = true;
  let scheduled = false;
  const run = () => {
    scheduled = false;
    syncSelect2ComboboxHaspopup();
    syncSelect2Labels();
    fixSelect2NestedResultsLists();
  };
  const mo = new MutationObserver(() => {
    if (!scheduled) {
      scheduled = true;
      window.requestAnimationFrame(run);
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
  run();
}

class Select2 extends DrupalBehavior {
  onContent(context) {

    initSelect2WcagObserver();

    $(document)
      .off('select2:open.summitWcag select2:close.summitWcag')
      .on('select2:open.summitWcag', 'select', function () {
        const $select = $(this);
        applySelect2ListboxAccessibility($select);
        window.requestAnimationFrame(() => {
          fixSelect2NestedResultsLists();
        });
      })
      .on('select2:close.summitWcag', 'select', function () {
        clearSelect2ListboxControls($(this));
      });

    syncSelect2ComboboxHaspopup();
    syncSelect2Labels();

    const $service = $('#edit-service');

    function syncSelect2WithModal($el) {

      // select2 relies heavily on the input focusing.
      // Make sure opening of the modal doesn't prevent select2 from triggering because:
      //   1. Modal's `position:fixed` makes select2 think it's off screen and therefore won't open
      //   2. Modal relying on input's focus makes select2 jealous and won't open
      // One modal is, select2 must behave differently.

      // OPEN
      $el.off('select2:opening.bypass')
        .on('select2:opening.bypass', function (e) {
          const $self = $(this);

          // If desktop, be normal
          if (!isMobile()) return;

          // If mobile, incercept so we can trigger modalOpen() without interfering
          if ($self.data('s2Bypass')) return;

          e.preventDefault();
          $self.data('s2Bypass', true);

          if (!$('body').hasClass('search-modal--is-open')) {
            modalOpen();
          }

          $self.select2('open');
          $self.data('s2Bypass', false);
        });

      // CLOSE guard (don’t close while modal open on mobile unless forced)
      $el.off('select2:closing.bypass')
        .on('select2:closing.bypass', function (e) {
          const $self = $(this);

          // Was this close triggered by ESC?
          const orig = e.params && e.params.args && e.params.args.originalEvent;
          const isEsc = !!orig && (
            (typeof orig.key === 'string' && orig.key.toLowerCase() === 'escape') ||
            orig.keyCode === 27
          );

          // We also support our manual force-close flag (cancel button / ESC capture etc.)
          const forceClose = isEsc || !!$self.data('s2ForceClose');

          // Guard: on mobile + modal open, prevent closing unless ESC/forced
          if (isMobile() && $('body').hasClass('search-modal--is-open') && !forceClose) {
            e.preventDefault();
            return;
          }

          // If ESC triggered this, also close the modal after Select2 finishes
          if (isEsc) {
            setTimeout(() => {
              modalClose();
              $('.search-modal').removeClass('search-modal--autocomplete-open');
            }, 0);
          }
        });
    }

    function modalClose() {
      $('.search-modal', context).removeClass('search-modal--open');
      $('body').removeClass('search-modal--is-open');
    }

    function modalOpen() {
      $('.search-modal', context).addClass('search-modal--open');
      $('body').addClass('search-modal--is-open');
    }

    function isMobile() { return $(window).width() <= 768; }

    let wasMobile = isMobile();

    function handleViewportChange() {
      const nowMobile = isMobile();
      if (wasMobile && !nowMobile) modalClose();
      const placeholderAttr = nowMobile ? 'data-placeholder-mobile' : 'data-placeholder';
      const placeholder = $service.attr(placeholderAttr);
      $('.select2-search__field').attr('placeholder', placeholder);

      const ariaLabel = $service.attr('data-aria-label');
      $('.select2-search__field').attr('aria-label', ariaLabel);

      wasMobile = nowMobile;
    }

    function bindCancelButton($el) {
      $(document).on('click', '.search-modal-cancel-button', function () {
        $el.data('s2ForceClose', true);
        try { $el.select2('close'); } catch (e) { }
        setTimeout(() => $el.data('s2ForceClose', false), 0);

        modalClose();
        setTimeout(() => {
          $('.search-modal').removeClass('search-modal--autocomplete-open');
        }, 10);
      });
    }

    // ESC should not closes autocomplete when modal is open
    function bindEscToClose($el) {

      // Unbind first (idempotent)
      $(document).off('keydown.searchModalEsc', '.select2-search__field');

      // Delegate to the Select2 search input
      $(document).on('keydown.searchModalEsc', '.select2-search__field', function (ev) {

        if ((ev.key !== 'Escape' && ev.keyCode !== 27)) return;
        if (!isMobile() || !$('body').hasClass('search-modal--is-open')) return;

        ev.preventDefault();
        ev.stopImmediatePropagation();

        // force-close Select2, then close modal
        $el.data('s2ForceClose', true);
        try { $el.select2('close'); } catch (e) { }
        setTimeout(() => $el.data('s2ForceClose', false), 0);

        modalClose();
        setTimeout(() => {
          $('.search-modal').removeClass('search-modal--autocomplete-open');
        }, 10);
      });
    }

    // init
    handleViewportChange();
    syncSelect2WithModal($service);
    bindCancelButton($service);
    bindEscToClose($service);

    $(window).on('resize', handleViewportChange);

    $(document).on('select2:close', '#edit-service', function () {
      modalClose();
      setTimeout(() => {
        $('.search-modal').removeClass('search-modal--autocomplete-open');
      }, 10);
    });
  }
}

DrupalBehavior.register('summit_theme', 'Select2', Select2);
